import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControlLabel,
  Switch,
  Stack,
  Card as MuiCard,
  CardContent,
  Link,
  Typography,
  Chip,
} from "@mui/material";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import axios from "axios";
import Notification from "./Notification";
import CountryRequirementsSection from "./CountryRequirementsSection";
import CountrySelectionPopup from "./CountrySelectionPopup";

const Card = styled(MuiCard)(spacing);

const AddDocumentType = ({ onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [notify, setNotify] = useState({ isOpen: false, message: "", type: "" });
  const [formData, setFormData] = useState({
    name: "",
    shortCode: "",
    description: "",
    vatRequired: false,
    edRequired: false,
    hasTimeRestriction: false,
    // New fields for country-specific requirements
    enableCountrySpecificRequirements: false,
    countryRequirements: [],
    // New fields for VAT and ED country selection
    vatRequiredCountries: [],
    edRequiredCountries: [],
  });
  const [countryPopupOpen, setCountryPopupOpen] = useState(false);
  const [countryPopupType, setCountryPopupType] = useState(""); // "vat" or "ed"
  const [tempSelectedCountries, setTempSelectedCountries] = useState([]); // Temporary selection in popup

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({
      name: "",
      shortCode: "",
      description: "",
      vatRequired: false,
      edRequired: false,
      hasTimeRestriction: false,
      // New fields for country-specific requirements
      enableCountrySpecificRequirements: false,
      countryRequirements: [],
      // New fields for VAT and ED country selection
      vatRequiredCountries: [],
      edRequiredCountries: [],
    });
    setCountryPopupOpen(false);
    setCountryPopupType("");
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData({
      ...formData,
      [name]:
        name === "vatRequired" ||
        name === "edRequired" ||
        name === "hasTimeRestriction" ||
        name === "enableCountrySpecificRequirements"
          ? checked
          : value,
    });
  };

  const handleOpenCountryPopup = (type) => {
    setCountryPopupType(type);
    // If no countries are selected, select all countries
    if (type === "vat" && formData.vatRequiredCountries.length === 0) {
      // This will be handled by the popup component
    } else if (type === "ed" && formData.edRequiredCountries.length === 0) {
      // This will be handled by the popup component
    }
    setCountryPopupOpen(true);
  };

  const handleCountrySelection = (selectedCountryIds) => {
    // Update form data when selection is confirmed
    // selectedCountryIds are already strings
    if (countryPopupType === "vat") {
      setFormData({
        ...formData,
        vatRequiredCountries: selectedCountryIds,
      });
    } else if (countryPopupType === "ed") {
      setFormData({
        ...formData,
        edRequiredCountries: selectedCountryIds,
      });
    }
  };

  const handleCountryConfirm = () => {
    // Selection is updated via handleCountrySelection, just close the popup
    setCountryPopupOpen(false);
  };

  const handleCountryCancel = () => {
    // Don't update form data, just close popup
    setCountryPopupOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Send basic document type data
      const documentTypeData = {
        name: formData.name,
        shortCode: formData.shortCode,
        description: formData.description,
        vatRequired: formData.vatRequired,
        edRequired: formData.edRequired,
        hasTimeRestriction: formData.hasTimeRestriction,
        // Add country-specific VAT and ED requirements - ensure they are strings
        vatRequiredCountries: Array.isArray(formData.vatRequiredCountries)
          ? formData.vatRequiredCountries.join(",")
          : formData.vatRequiredCountries || "",
        edRequiredCountries: Array.isArray(formData.edRequiredCountries)
          ? formData.edRequiredCountries.join(",")
          : formData.edRequiredCountries || "",
      };

      const response = await axios.post("/api/v1/documenttypes", documentTypeData);
      const documentTypeId = response.data.documentTypeId;

      // If there are country-specific requirements, send these as well
      if (formData.enableCountrySpecificRequirements && formData.countryRequirements.length > 0) {
        // Gebruik de correcte endpoint: /api/v1/countryrequirements voor elke requirement
        for (const req of formData.countryRequirements) {
          try {
            const requestData = {
              documentTypeId: documentTypeId,
              countryId: req.countryId,
              HasTimRestriction: req.hasTimeRestriction, // Match backend model field name
              specialRequirements: req.specialRequirements,
            };

            await axios.post("/api/v1/countryrequirements", requestData);
          } catch (error) {
            console.error("Error creating country requirement:", error);
            // Continue met andere requirements, maar log de error
          }
        }
      }

      setNotify({
        isOpen: true,
        message: "Document Type Added Successfully",
        type: "success",
      });
      handleClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      setNotify({
        isOpen: true,
        message: "Error adding document type",
        type: "error",
      });
    }
  };

  return (
    <Card mb={0}>
      <CardContent>
        <Button variant="contained" color="primary" onClick={handleClickOpen}>
          Add Document Type
        </Button>
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
          <DialogTitle>Add New Document Type</DialogTitle>
          <DialogContent>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                margin="normal"
                inputProps={{ maxLength: 50 }}
              />
              <TextField
                fullWidth
                label="Short Code"
                name="shortCode"
                value={formData.shortCode}
                onChange={handleChange}
                required
                margin="normal"
                inputProps={{ maxLength: 3 }}
              />
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                margin="normal"
                multiline
                rows={3}
                inputProps={{ maxLength: 250 }}
              />
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr 600px", gap: 1, alignItems: "center", mb: 1 }}>
                  <Switch checked={formData.vatRequired} onChange={handleChange} name="vatRequired" />
                  <Typography variant="body2">VAT Required</Typography>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={(e) => {
                      e.preventDefault();
                      if (formData.vatRequired) {
                        handleOpenCountryPopup("vat");
                      }
                    }}
                    sx={{
                      textDecoration: "none",
                      fontSize: "0.875rem",
                      padding: 0,
                      justifySelf: "start",
                      textAlign: "left",
                      opacity: formData.vatRequired ? 1 : 0.5,
                      cursor: formData.vatRequired ? "pointer" : "not-allowed",
                    }}
                  >
                    Select Countries
                  </Link>
                </Box>
                {formData.vatRequiredCountries.length > 0 && (
                  <Box sx={{ ml: 4, mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Selected countries: {formData.vatRequiredCountries.length}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr 600px", gap: 1, alignItems: "center", mb: 1 }}>
                  <Switch checked={formData.edRequired} onChange={handleChange} name="edRequired" />
                  <Typography variant="body2">ED Required</Typography>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={(e) => {
                      e.preventDefault();
                      if (formData.edRequired) {
                        handleOpenCountryPopup("ed");
                      }
                    }}
                    sx={{
                      textDecoration: "none",
                      fontSize: "0.875rem",
                      padding: 0,
                      justifySelf: "start",
                      textAlign: "left",
                      opacity: formData.edRequired ? 1 : 0.5,
                      cursor: formData.edRequired ? "pointer" : "not-allowed",
                    }}
                  >
                    Select Countries
                  </Link>
                </Box>
                {formData.edRequiredCountries.length > 0 && (
                  <Box sx={{ ml: 4, mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Selected countries: {formData.edRequiredCountries.length}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr 600px", gap: 1, alignItems: "center", mb: 1 }}>
                  <Switch checked={formData.hasTimeRestriction} onChange={handleChange} name="hasTimeRestriction" />
                  <Typography variant="body2">Has Time Restriction</Typography>
                  <Box />
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr 600px", gap: 1, alignItems: "center", mb: 1 }}>
                  <Switch
                    checked={formData.enableCountrySpecificRequirements}
                    onChange={handleChange}
                    name="enableCountrySpecificRequirements"
                  />
                  <Typography variant="body2">Enable Country-Specific Requirements</Typography>
                  <Box />
                </Box>
              </Box>

              {formData.enableCountrySpecificRequirements && (
                <CountryRequirementsSection
                  requirements={formData.countryRequirements}
                  onChange={(requirements) => setFormData({ ...formData, countryRequirements: requirements })}
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained" color="primary">
              Add
            </Button>
          </DialogActions>
        </Dialog>

        <CountrySelectionPopup
          open={countryPopupOpen}
          onClose={handleCountryCancel}
          onConfirm={handleCountrySelection}
          title={countryPopupType === "vat" ? "Select Countries for VAT Required" : "Select Countries for ED Required"}
          selectedCountries={countryPopupType === "vat" ? formData.vatRequiredCountries : formData.edRequiredCountries}
          defaultSelectAll={true}
        />

        <Notification notify={notify} setNotify={setNotify} />
      </CardContent>
    </Card>
  );
};

export default AddDocumentType;
